import { cloneDeep, isEmpty } from "lodash-es";
import type { MaybeRef } from "vue";
import { isRef } from "vue";
import type { LocationQuery, RouteLocationRaw, Router } from "vue-router";

import type { SearchFilter, User } from "@/types";

import { NgProviderHelper, SearchFilterHelper } from ".";

export class SearchRouteHelper {
  private readonly router: Router;
  private readonly user: User;

  constructor(router: Router, user: MaybeRef<User>) {
    this.router = router;
    this.user = isRef(user) ? user.value : user;
  }

  public getSearchRouteName() {
    const meta = this.router.currentRoute.value.meta;

    // Stay on the already picked search view type, i.e. classic or compact
    if (this.router.currentRoute.value.name && meta.searchViewType) {
      return this.router.currentRoute.value.name;
    }

    // Go directly to the user's preferred search view type to enable smooth transition
    if (this.user.settings.preferredSearchView === "compact") {
      return "search.compact";
    }

    return "search.classic";
  }

  private getSearchEntryRouteName() {
    return "search";
  }

  public getStaticMetadata(routeQuery: LocationQuery): Record<string, string> {
    return (routeQuery.staticMetadata as unknown as Record<string, string>) || {};
  }

  public buildNextStaticMetadata(direction: string | undefined, staticMetadata?: string): Record<string, string> {
    const currentStaticMetadata = this.getStaticMetadata(this.router.currentRoute.value.query);
    const nextStaticMetadata = !isEmpty(currentStaticMetadata) ? cloneDeep(currentStaticMetadata) : {};

    if (staticMetadata && direction) {
      nextStaticMetadata[direction] = staticMetadata;
    }

    return nextStaticMetadata;
  }

  public getTemporaryMetadata(routeQuery: LocationQuery): Record<string, string[]> {
    return (routeQuery.temporaryMetadata as unknown as Record<string, string[]>) || {};
  }

  public buildNextTemporaryMetadata(
    direction: string | undefined,
    temporaryMetadata?: string[],
    resetTemporaryMetadata = false
  ): Record<string, string[]> {
    if (resetTemporaryMetadata) {
      return temporaryMetadata && direction ? { [direction]: temporaryMetadata } : {};
    }

    const currentTemporaryMetadata = this.getTemporaryMetadata(this.router.currentRoute.value.query);
    const nextTemporaryMetadata = !isEmpty(currentTemporaryMetadata) ? cloneDeep(currentTemporaryMetadata) : {};

    if (temporaryMetadata && direction) {
      nextTemporaryMetadata[direction] = temporaryMetadata;
    }

    return nextTemporaryMetadata;
  }

  public generateUrlWithBase(to: RouteLocationRaw) {
    const base = this.router.options.history.base;
    const location = this.router.resolve(to).fullPath;

    return `${base}${location}`;
  }

  public generateDictionarySearchUrl(dictionaryId: string): RouteLocationRaw {
    return {
      name: this.getSearchRouteName(),
      query: {
        ...this.router.currentRoute.value.query,
        dictionaryId,
        allowSwitchDictionary: 0,
        section: undefined
      }
    };
  }

  public generateQuerySearchUrl(query: string): RouteLocationRaw {
    return {
      name: this.getSearchRouteName(),
      query: {
        ...this.router.currentRoute.value.query,
        query,
        allowSwitchDictionary: 0,
        section: undefined
      }
    };
  }

  public generateResetSearchUrl(): RouteLocationRaw {
    return {
      name: this.getSearchRouteName(),
      query: {
        query: this.router.currentRoute.value.query.query,
        filter: this.router.currentRoute.value.query.filter
      }
    };
  }

  public resetSearchType() {
    const route = {
      name: this.getSearchEntryRouteName(),
      query: {
        query: this.router.currentRoute.value.query.query,
        filter: this.router.currentRoute.value.query.filter
      }
    } as unknown as RouteLocationRaw;

    this.router.push(route);
  }

  public search(
    query: string,
    filter?: SearchFilter,
    dictionaryId?: string,
    staticMetadata?: string,
    temporaryMetadata?: string[],
    section?: string,
    resetTemporaryMetadata = true,
    allowSwitchDictionary = true
  ) {
    // For all dictionaries with metadata, the direction matches the dictionaryId
    // except for ordbogen-daar - so apply a quick-fix for that dictionary
    let direction = dictionaryId;
    if (direction === "ordbogen-daar") {
      direction = "ordbogen-arda";
    }

    const nextStaticMetadata = this.buildNextStaticMetadata(direction, staticMetadata);
    const nextTemporaryMetadata = this.buildNextTemporaryMetadata(direction, temporaryMetadata, resetTemporaryMetadata);

    const route = {
      name: this.getSearchRouteName(),
      query: {
        query,
        dictionaryId,
        allowSwitchDictionary: allowSwitchDictionary ? undefined : 0,
        filter: filter ? SearchFilterHelper.prepareForSerialization(filter) : undefined,
        staticMetadata: nextStaticMetadata,
        temporaryMetadata: nextTemporaryMetadata,
        section: section
      }
    } as unknown as RouteLocationRaw;

    this.broadcastRootScopeDestroy();

    return this.router.push(route);
  }

  public searchWithSearchFilter(
    query: string,
    filter: SearchFilter,
    dictionaryId?: string,
    allowSwitchDictionary = true
  ) {
    this.search(query, filter, dictionaryId, undefined, undefined, undefined, true, allowSwitchDictionary);
  }

  public searchWithChangedSearchFilter(query: string, filter: SearchFilter, dictionaryId: string) {
    this.search(query, filter, dictionaryId, undefined, undefined, undefined, false, false);
  }

  public selectDictionary(
    dictionaryId: string | undefined,
    query?: string | undefined,
    direction?: string | undefined,
    allowSwitchDictionary = false,
    method: "push" | "replace" = "push"
  ) {
    this.broadcastRootScopeDestroy();

    return this.router[method]({
      name: this.getSearchRouteName(),
      query: {
        ...this.router.currentRoute.value.query,
        query: query ?? this.router.currentRoute.value.query.query,
        dictionaryId,
        direction,
        allowSwitchDictionary: allowSwitchDictionary ? undefined : 0,
        section: undefined
      }
    });
  }

  public deselectDictionary() {
    this.broadcastRootScopeDestroy();
    return this.router.push(this.generateResetSearchUrl());
  }

  public getSearchWord(): string {
    const query = this.router.currentRoute.value.query.query;

    if (query) {
      return query.toString();
    }

    return "";
  }

  private broadcastRootScopeDestroy(): void {
    const $rootScope = NgProviderHelper.getInstance("$rootScope");
    $rootScope.$broadcast("$destroy");
  }
}
